import DOMPurify from 'dompurify';
import { ErrorNotice } from '@bcecloud/portal-ui';
import { placeholderToken, previewText, type OutreachPlaceholder, type RepSignature } from '@/lib/outreach-templates';

export interface TemplatePreviewProps {
  readonly subject: string;
  readonly body: string;
  readonly placeholders: readonly OutreachPlaceholder[];
  /** The rep's Salesforce signature, shown under the email; undefined while it loads. */
  readonly signature?: RepSignature | undefined;
  /** The heading; sample values by default, the client's own on the compose page. */
  readonly title?: string;
}

/**
 * The template as a client would read it, with sample values in place of
 * the placeholders. A placeholder nobody knows (a typo) stays as written and
 * is called out, so the rep fixes it before a draft ever uses it.
 *
 * @param props - The subject, the body and the known placeholders.
 * @returns The preview.
 */
export function TemplatePreview({ subject, body, placeholders, signature, title = 'Preview with sample values' }: TemplatePreviewProps): React.ReactElement {
  const filledSubject = previewText(subject, placeholders);
  const filledBody = previewText(body, placeholders);
  const unknown = [...new Set([...filledSubject.unknown, ...filledBody.unknown])];

  return (
    <section aria-label={title} className="rounded-portal-card border border-portal-line bg-portal-bg p-4">
      <p className="text-portal-micro font-bold text-portal-muted uppercase">{title}</p>
      <p className="mt-2 text-portal-small font-bold text-portal-ink">{filledSubject.text || 'No subject yet'}</p>
      <p className="mt-2 text-portal-small leading-[1.6] whitespace-pre-line text-portal-ink">
        {filledBody.text || 'The email shows here as you write it.'}
      </p>
      {signature && signature.signature ? (
        <div className="mt-4 border-t border-portal-line pt-3 text-portal-small text-portal-ink" aria-label="Your signature">
          {signature.isHtml ? (
            // Sanitized: the signature comes from Salesforce, so scripts, handlers and javascript: links are stripped.
            <div className="[&_img]:max-h-24 [&_img]:max-w-full" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(signature.signature) }} />
          ) : (
            <p className="whitespace-pre-line">{signature.signature}</p>
          )}
        </div>
      ) : signature ? (
        <p className="mt-4 border-t border-portal-line pt-3 text-portal-micro text-portal-muted">
          No signature in Salesforce. Add one under your Salesforce settings (My Email Settings) and it shows here.
        </p>
      ) : null}
      {unknown.length === 0 ? null : (
        <div className="mt-3">
          <ErrorNotice
            inline
            tone="warning"
            message={`Not a placeholder: ${unknown.map(placeholderToken).join(', ')}. Pick one from the list instead, or the client sees it as written.`}
          />
        </div>
      )}
    </section>
  );
}
